import React, { useState, useEffect } from 'react';
import { Box, Typography, styled } from '@mui/material';
import { useStore } from 'zustand';

import { useTheme } from '../../contexts/ThemeContext';

// Icons for each annotation type
import EditNoteIcon from '@mui/icons-material/EditNote';
import RectangleIcon from '@mui/icons-material/Rectangle';
import CircleIcon from '@mui/icons-material/Circle';
import TimelineIcon from '@mui/icons-material/Timeline';
import PolylineIcon from '@mui/icons-material/Polyline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import LinkIcon from '@mui/icons-material/Link';
import { ProtoAnnotationType } from '../annotations/types';
import { useAnnotationStore } from '../annotations/annotationStore';

const TooltipContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ theme, darkMode }) => ({
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 9999,
  backgroundColor: darkMode ? 'rgba(45, 45, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  color: darkMode ? '#ffffff' : '#333333',
  border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
  borderRadius: '6px',
  padding: '6px 10px',
  fontSize: '12px',
  fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  fontWeight: 500,
  boxShadow: darkMode ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: '80px',
  transform: 'translate(12px, -32px)', // Offset from cursor
  userSelect: 'none',
  transition: 'opacity 0.15s ease, transform 0.1s ease',
  animation: 'tooltipFadeIn 0.2s ease-out',
  '@keyframes tooltipFadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translate(8px, -28px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translate(12px, -32px) scale(1)',
    },
  },
}));

const StyledIcon = styled('div')<{ darkMode: boolean }>(({ darkMode }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  color: darkMode ? '#81c784' : '#2e7d32',
}));

// Mapping of annotation types to their display info
const getAnnotationInfo = (
  type: ProtoAnnotationType,
  opts?: { isHyperlinkAwaitingDestination?: boolean }
) => {
  switch (type) {
    case 'text':
      return {
        icon: EditNoteIcon,
        label: 'Text Note',
        shortcut: 'Click to place',
      };
    case 'rectangle':
      return {
        icon: RectangleIcon,
        label: 'Rectangle',
        shortcut: 'Drag to define',
      };
    case 'circle':
      return {
        icon: CircleIcon,
        label: 'Circle',
        shortcut: 'Drag to define',
      };
    case 'ellipse':
      return {
        icon: CircleIcon,
        label: 'Ellipse',
        shortcut: 'Drag to define',
      };
    case 'line':
      return {
        icon: TimelineIcon,
        label: 'Line',
        shortcut: 'Drag to define',
      };
    case 'polyline':
      return {
        icon: PolylineIcon,
        label: 'Polyline',
        shortcut: 'Click points • Enter to finish',
      };
    case 'polygon':
      return {
        icon: PolylineIcon, // Using same icon as polyline since they're similar
        label: 'Polygon',
        shortcut: 'Click points • Enter to finish',
      };
    case 'arrow':
      return {
        icon: ArrowForwardIcon,
        label: 'Arrow',
        shortcut: 'Drag to define',
      };
    case 'highlighter':
      return {
        icon: FormatColorFillIcon,
        label: 'Highlighter',
        shortcut: 'Drag to define',
      };
    case 'hyperlink':
      return {
        icon: LinkIcon,
        label: 'Link',
        shortcut: opts?.isHyperlinkAwaitingDestination
          ? 'Place destination link'
          : 'Place link on page',
      };
    default:
      return null;
  }
};

export const CursorTooltip: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    isAnnotating,
    activeAnnotationType,
    showCursorTooltip,
    isPastePreviewActive,
    pastePreviewAnnotation,
    cutOriginalAnnotation,
    isHyperlinkAwaitingDestination,
  } = useStore(useAnnotationStore);
  const [screenPosition, setScreenPosition] = useState({ x: 0, y: 0 });

  // Track mouse position in screen coordinates
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setScreenPosition({ x: e.clientX, y: e.clientY });
    };

    if ((isAnnotating && activeAnnotationType) || isPastePreviewActive) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [isAnnotating, activeAnnotationType, isPastePreviewActive]);

  // Show tooltip when actively annotating or during paste preview
  if (!showCursorTooltip) {
    return null;
  }

  // Handle paste preview mode
  if (isPastePreviewActive && pastePreviewAnnotation) {
    return (
      <TooltipContainer
        darkMode={darkMode}
        style={{
          left: `${screenPosition.x}px`,
          top: `${screenPosition.y}px`,
        }}
      >
        <StyledIcon darkMode={darkMode}>
          <FormatColorFillIcon fontSize="inherit" />
        </StyledIcon>
        <Box>
          <Typography
            variant="caption"
            component="div"
            sx={{
              lineHeight: 1.2,
              fontWeight: 600,
              fontSize: '11px',
            }}
          >
            {cutOriginalAnnotation ? 'Cut Preview' : 'Paste Preview'}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{
              lineHeight: 1.1,
              opacity: 0.7,
              fontSize: '9px',
              fontStyle: 'italic',
            }}
          >
            {cutOriginalAnnotation
              ? 'Click to paste & delete original • Press Esc to cancel & restore'
              : 'Click to paste • Press Esc to cancel'}
          </Typography>
        </Box>
      </TooltipContainer>
    );
  }

  // Handle normal annotation mode
  if (!isAnnotating || !activeAnnotationType) {
    return null;
  }

  const annotationInfo = getAnnotationInfo(activeAnnotationType, {
    isHyperlinkAwaitingDestination,
  });
  if (!annotationInfo) {
    return null;
  }

  const IconComponent = annotationInfo.icon;

  // Calculate position relative to viewport
  const tooltipStyle = {
    left: `${screenPosition.x}px`,
    top: `${screenPosition.y}px`,
  };

  return (
    <TooltipContainer darkMode={darkMode} style={tooltipStyle}>
      <StyledIcon darkMode={darkMode}>
        <IconComponent fontSize="inherit" />
      </StyledIcon>
      <Box>
        <Typography
          variant="caption"
          component="div"
          sx={{
            lineHeight: 1.2,
            fontWeight: 600,
            fontSize: '11px',
          }}
        >
          {annotationInfo.label}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{
            lineHeight: 1.1,
            opacity: 0.7,
            fontSize: '9px',
            fontStyle: 'italic',
          }}
        >
          {annotationInfo.shortcut}
        </Typography>
      </Box>
    </TooltipContainer>
  );
};
